import React, { useCallback, useEffect, useState } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import InfoItem from '../../../components/WhatsApp/InfoItem';
import TemplateList from '../../../components/WhatsApp/TemplateList';
import UpdateBusinessModal from '../../../components/WhatsApp/UpdateBusinessModal';
import WebhookUrlModal from '../../../components/WhatsApp/WebhookUrlModal';
import SidebarSecondary from '../../../components/ui/sidebar-secondary/SidebarSecondary';
import { getRequestAsync, postRequestAsync } from '../../../helpers/apiHandler';
import useFBAuthenticated from '../../../hooks/useFBAuthenticated';
import { showAlert } from '../../../store/actions/alertAction';
import SidebarDetails from './SidebarDetails';

const WhatsAppNumberDetails = () => {
  const { waba_id, phone_number_id } = useParams();
  const navigate = useNavigate();
  const { access_token } = useSelector((state) => state.auth.user);
  const { isAuthenticated: isFBAuthenticated, isLoading: isFBAuthLoading } =
    useFBAuthenticated(access_token);
  const [phoneDetails, setPhoneDetails] = useState({});
  const [showBusinessUpdateModal, setShowBusinessUpdateModal] = useState(false);
  const [showWebhookUrlModal, setShowWebhookUrlModal] = useState(false);
  const dispatch = useDispatch();
  const [isLoaded, setIsLoaded] = useState(false);

  const [businessProfileData, setBusinessProfileData] = useState({
    messaging_product: 'whatsapp',
    address: '',
    description: '',
    vertical: '',
    about: '',
    email: '',
    websites: [],
  });

  // Get business profile data from phoneDetails
  const getBusinessProfileData = async () => {
    try {
      const data = {
        headers: { Authorization: access_token.token },
        params: { phone_number_id: phone_number_id },
      };

      const res = await getRequestAsync(`/v2/whatsapp/get-business-profile`, data);

      if (res.success) {
        setBusinessProfileData(res.data.profile);
      }
    } catch (error) {
      console.error('Error fetching business profile:', error);
      dispatch(showAlert('Failed to fetch business profile!', 'danger'));
    }
  };

  const handleSubmit = async (formData, { setLoading }) => {
    try {
      console.log('Submitting form data:', formData);
      const payloadData = {
        messaging_product: formData.messaging_product,
        address: formData.address,
        description: formData.description,
        vertical: formData.vertical,
        about: formData.about,
        email: formData.email,
        websites: Array.isArray(formData.websites)
          ? formData.websites
          : [formData.websites].filter(Boolean),
        phone_number_id,
      };

      const res = await postRequestAsync(
        '/v2/whatsapp/update-business-profile',
        payloadData,
        access_token.token
      );

      if (res.success) {
        await fetchPhoneDetails();
        setShowBusinessUpdateModal(false);
        dispatch(showAlert('Business profile has been updated!', 'success'));
      } else {
        console.error('Error updating business profile:', res);
        dispatch(
          showAlert(
            `Business profile failed to update: ${res.message || 'Please try again later.'}`,
            'danger'
          )
        );
      }
    } catch (error) {
      console.error('Error updating business profile:', error);
      dispatch(
        showAlert(
          'Something went wrong while updating business profile. Please try again later.',
          'danger'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEditWebhookUrl = () => {
    setShowWebhookUrlModal(true);
  };

  const handleWebhookUrlSubmit = async (data) => {
    if (data.success) {
      dispatch(showAlert(data.message || 'Webhook URL has been updated successfully!', 'success'));
      await fetchPhoneDetails();
    } else {
      dispatch(
        showAlert(data.message || 'Failed to update webhook URL. Please try again.', 'danger')
      );
    }
    setShowWebhookUrlModal(false);
  };

  const fetchPhoneDetails = useCallback(async () => {
    try {
      const res = await postRequestAsync(
        '/v2/whatsapp/get-phone-details',
        { phone_number_id },
        access_token.token
      );

      if (res.success) {
        const { data } = res.data;
        setPhoneDetails(data);
        return res;
      }
      dispatch(showAlert('Failed to fetch phone details!', 'danger'));
      return null;
    } catch (error) {
      console.error('Error fetching phone details:', error);
      dispatch(showAlert('Failed to fetch phone details!', 'danger'));
      return null;
    }
  }, [phone_number_id, access_token, dispatch]);

  const generateApiKey = async () => {
    try {
      const res = await postRequestAsync('/v2/whatsapp/generate-api-key', {}, access_token.token);
      if (res.success) {
        await fetchPhoneDetails();
      } else {
        dispatch(showAlert('Failed to generate API Key!', 'danger'));
        console.log('Error generating api token');
      }
    } catch (error) {
      console.error('Error generating API key:', error);
      dispatch(showAlert('Failed to generate API Key!', 'danger'));
    }
  };

  const deleteApiKey = async () => {
    try {
      const res = await postRequestAsync('/v2/whatsapp/delete-api-key', {}, access_token.token);
      if (res.success) {
        await fetchPhoneDetails();
      } else {
        dispatch(showAlert('Failed to delete API Key!', 'danger'));
        console.log('Error deleting api token');
      }
    } catch (error) {
      console.error('Error deleting API key:', error);
      dispatch(showAlert('Failed to delete API Key!', 'danger'));
    }
  };

  const copyToClipboard = () => {
    if (phoneDetails.api_key) {
      navigator.clipboard
        .writeText(phoneDetails.api_key)
        .then(() => {
          dispatch(showAlert('API Key copied to clipboard!', 'success'));
        })
        .catch((err) => {
          dispatch(showAlert('Failed to copy API Key to clipboard!', 'danger'));
          console.error('Failed to copy: ', err);
        });
    }
  };

  const handleApiKeyAction = (action) => {
    if (phoneDetails.api_key) {
      if (action === 'regenerate') {
        if (
          window.confirm(
            'Are you sure you want to regenerate the API key? This will invalidate the current key.'
          )
        ) {
          generateApiKey();
        }
      } else if (action === 'delete') {
        if (
          window.confirm(
            'Are you sure you want to delete the API key? This action cannot be undone.'
          )
        ) {
          deleteApiKey();
        }
      } else {
        // Default action is to copy to clipboard
        copyToClipboard();
      }
    } else {
      generateApiKey();
    }
  };

  useEffect(() => {
    if (!isFBAuthLoading && !isFBAuthenticated) {
      navigate('/dashboard/whatsapp/getting-started');
    }
  }, [isFBAuthenticated, isFBAuthLoading, navigate]);

  useEffect(() => {
    const initializeData = async () => {
      if (isFBAuthenticated && !isLoaded) {
        try {
          setIsLoaded(true);

          const phoneData = await fetchPhoneDetails();

          if (phoneData && phoneData.success) {
            await getBusinessProfileData();
          }
        } catch (error) {
          console.error('Error initializing data:', error);
          setIsLoaded(false);
        }
      }
    };

    initializeData();
  }, [isFBAuthenticated, isLoaded, access_token, phone_number_id, dispatch]);

  return (
    <div className="whatsapp-page number-details d-flex has-sidebar-secondary">
      <UpdateBusinessModal
        show={showBusinessUpdateModal}
        onHide={() => setShowBusinessUpdateModal(false)}
        initialData={businessProfileData}
        onSubmit={handleSubmit}
      />
      <WebhookUrlModal
        show={showWebhookUrlModal}
        onHide={() => setShowWebhookUrlModal(false)}
        initialUrl={phoneDetails?.webhook_configuration?.application}
        wabaId={waba_id}
        phoneNumberId={phone_number_id}
        onSubmit={handleWebhookUrlSubmit}
      />
      <SidebarSecondary>
        <SidebarDetails />
      </SidebarSecondary>
      <div className="page-content" style={{ padding: '40px' }}>
        <Row className="mb-4">
          <Col md={12}>
            <Card className="h-100 border-0" style={{ borderRadius: '20px' }}>
              <Card.Body className="p-4" style={{ borderRadius: '20px' }}>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="mb-0 fw-bold" style={{ fontSize: '20px' }}>
                    Basic Info
                  </h5>
                </div>
                {phoneDetails && (
                  <div className="d-flex flex-column" style={{ gap: '20px' }}>
                    <Row>
                      <Col>
                        <InfoItem label="Phone Number ID" value={phoneDetails.id} copyable />
                      </Col>
                      <Col>
                        <InfoItem label="Verified Name" value={phoneDetails.verified_name} />
                      </Col>
                      <Col>
                        <InfoItem
                          label="Display Phone Number"
                          value={phoneDetails.display_phone_number}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <InfoItem
                          label="Quality Rating"
                          value={phoneDetails.quality_rating}
                          badge
                          variant={
                            phoneDetails.quality_rating === 'HIGH'
                              ? 'success'
                              : phoneDetails.quality_rating === 'MEDIUM'
                              ? 'warning'
                              : 'danger'
                          }
                        />
                      </Col>
                      <Col>
                        <InfoItem
                          label="Code Verification Status"
                          value={phoneDetails.code_verification_status}
                          badge
                          variant={
                            phoneDetails.code_verification_status === 'VERIFIED'
                              ? 'success'
                              : 'warning'
                          }
                        />
                      </Col>
                      <Col>
                        <InfoItem
                          label="Throughput Level"
                          value={phoneDetails.throughput?.level}
                          badge
                          variant={
                            phoneDetails.throughput?.level === 'HIGH'
                              ? 'success'
                              : phoneDetails.throughput?.level === 'MEDIUM'
                              ? 'warning'
                              : 'danger'
                          }
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <InfoItem
                          label="Webhook URL"
                          value={phoneDetails.webhook_configuration?.application || 'Generate'}
                          copyable={!!phoneDetails.webhook_configuration?.application}
                          editable={!!phoneDetails.webhook_configuration?.application}
                          handleEdit={handleEditWebhookUrl}
                          clickable={!phoneDetails.webhook_configuration?.application}
                          handleClick={handleEditWebhookUrl}
                          isWebhookUrl={true}
                        />
                      </Col>
                      <Col>
                        <InfoItem
                          label="API Key"
                          value={phoneDetails.api_key ? phoneDetails.api_key : 'Generate'}
                          copyable={!!phoneDetails.api_key}
                          clickable={!phoneDetails.api_key}
                          handleClick={handleApiKeyAction}
                          isApiKey={!!phoneDetails.api_key}
                        />
                      </Col>
                      <Col>
                        <InfoItem
                          label="WhatsApp Business Profile"
                          value="Update"
                          clickable
                          handleClick={async () => {
                            await getBusinessProfileData();
                            setShowBusinessUpdateModal(true);
                          }}
                        />
                      </Col>
                    </Row>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <TemplateList phoneNumberId={phone_number_id} wabaId={waba_id} />
      </div>
    </div>
  );
};

export default WhatsAppNumberDetails;
